import React from 'react'
import { InferGetServerSidePropsType, NextPage } from 'next'
import { setCacheHeaders } from '@scentregroup/shared/helpers/set-cache-headers'
import { Box } from '@hub/box'
import CentreMap from '@scentregroup/shared/components/centre-map'
import { completelyCanonicalUrl } from '@scentregroup/shared/helpers/canonical-link'
import { useGetHrefContext } from '@scentregroup/shared/context'
import { localeToCountry } from '@scentregroup/shared/helpers/local-to-country'
import { useStoredCentre } from '@scentregroup/shared/hooks/use-stored-centre'
import {
  GetServerSidePropsContext,
  GetServerSidePropsResult,
} from '../../../types/next'
import { makeApolloClient } from '../../../lib/apolloClient'
import {
  buildContactLinks,
  CentreDetails,
  getCentreDetails,
} from '../../../../../src/shared/helpers/centre'
import Layout from '../../../../../src/shared/hub-components/layout'
import { getStoresForCentre } from '../../../lib/get-stores-for-centre'
import { StoreDetails } from '@scentregroup/shared/types/browse-stores'
import {
  getTopStripProps,
  ITopStripProps,
} from '@scentregroup/shared/hub-components/top-strip'
import { shouldRedirectForMismatchedRoute } from '@scentregroup/shared/lib/redirects'
import {
  CentreStaticData,
  fetchCentreStaticData,
} from '@scentregroup/shared/helpers/fetch-header-static-data'

interface CentreMapPageProps {
  legacyId: string | null
  entryId: string | null
  waypointId: string | null
  headerStaticData: CentreStaticData
  centre: CentreDetails
  country: 'Australia' | 'New Zealand'
  topStripProps: ITopStripProps | null
  stores: StoreForMap[]
  selectedStoreTitle: string | null
}

export async function getServerSideProps(
  ctx: GetServerSidePropsContext<{
    centre: string
    slug: string[]
  }>
): Promise<GetServerSidePropsResult<CentreMapPageProps>> {
  setCacheHeaders(ctx.res)
  const country = localeToCountry(ctx.locale)
  const centreSlug = ctx.params?.centre
  const slugParts = ctx.params?.slug || []
  const entryId = Array.isArray(ctx.query?.entryId)
    ? ctx.query?.entryId[0]
    : ctx.query?.entryId || null

  const waypointId = Array.isArray(ctx.query?.waypointId)
    ? ctx.query?.waypointId[0]
    : ctx.query?.waypointId || null

  if (!centreSlug) {
    return {
      notFound: true,
    }
  }
  const LEVEL_DEPTH = 2
  if (slugParts.length > LEVEL_DEPTH) {
    // 404
    return {
      notFound: true,
    }
  }

  const apollo = makeApolloClient()

  const [legacyId = null] = slugParts

  const [centre, stores, headerStaticData, topStripProps] = await Promise.all([
    getCentreDetails(apollo, {
      slug: centreSlug,
      country: localeToCountry(ctx.locale),
    }),
    getStoresForCentre(apollo, centreSlug),
    fetchCentreStaticData(apollo, {
      country: localeToCountry(ctx.locale),
      centre: Array.isArray(centreSlug) ? centreSlug[0] : centreSlug,
    }),
    getTopStripProps(apollo, country, centreSlug),
  ])

  if (!centre) {
    return {
      notFound: true,
    }
  }
  const redirectForCentreSlug = shouldRedirectForMismatchedRoute(
    '/[centre]/centre-map/[[...slug]]',
    ctx.params,
    {
      centre: centre.slug,
    }
  )
  if (redirectForCentreSlug) {
    return redirectForCentreSlug
  }
  if (!stores.items.length) {
    return {
      notFound: true,
    }
  }

  const selectedStoreTitle =
    stores.items.find(
      store =>
        store.id === entryId ||
        store?.legacyIdentifier?.toString() === slugParts[0]
    )?.title || null

  return {
    props: {
      legacyId,
      entryId,
      waypointId,
      headerStaticData,
      centre,
      country,
      topStripProps,
      stores: transformStoresToV1(stores.items, centreSlug),
      selectedStoreTitle,
    },
  }
}

const CentreMapPage: NextPage<
  InferGetServerSidePropsType<typeof getServerSideProps>
> = ({
  legacyId,
  entryId,
  waypointId,
  centre,
  country,
  topStripProps,
  headerStaticData,
  stores,
  selectedStoreTitle,
}) => {
  const { title: centreName } = centre
  const canonical = completelyCanonicalUrl(useGetHrefContext()())
  const metaData = selectedStoreTitle
    ? {
        title: `${selectedStoreTitle} Location at Westfield ${centre.title}`,
        description: `Westfield ${centre.title} ${selectedStoreTitle} - Check map locations and directions to help you find ${selectedStoreTitle} in Westfield ${centre.title}.`,
        canonical,
      }
    : {
        title: `Westfield ${centreName} Centre Map | Map of Stores in Centre`,
        description: `Locate your favourite store using our handy centre map with level by level directories of stores, key facilities and amenities at Westfield ${centreName}.`,
        canonical,
      }

  const contactLinks = buildContactLinks(centre)
  useStoredCentre(centre)

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Layout
        showFooter={false}
        country={country}
        topStrip={topStripProps}
        centre={{
          ...centre,
          contactLinks,
        }}
        header={{
          variant: 'centre',
          data: headerStaticData,
        }}
        metaData={metaData}
        mainStyles={{ flex: 1 }}
      >
        <Box
          sx={{
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            position: 'relative',
          }}
        >
          <CentreMap
            centre={centre}
            legacyIdentifier={legacyId}
            entryId={entryId}
            waypointId={waypointId}
            stores={stores}
          />
        </Box>
      </Layout>
    </Box>
  )
}

export default CentreMapPage

interface StoreForMap {
  sys: { id: string }
  title: string
  slug: string
  landmarkDescription?: string | null
  logo?: {
    alt: string
    src: string
  }
  paymentMethods?: readonly string[]
  link: string
  legacyIdentifier?: number | null
}

function transformStoresToV1(
  stores: StoreDetails[],
  centreSlug: string
): StoreForMap[] {
  return stores.map(store => ({
    sys: { id: store.id },
    title: store.title,
    slug: store.slug,
    landmarkDescription: store.landmarkDescription,
    logo: {
      alt: `${store.slug}-logo`,
      src: store.logoUrl || '',
    },
    paymentMethods: store.paymentMethods || [],
    link: `/${centreSlug}/store/${store.id}/${store.slug}`,
    legacyIdentifier: store?.legacyIdentifier,
  }))
}
